import type { LogRecord, LogSink } from './types';

/**
 * No-op sink. The factory default in production when no other sink is
 * registered; keeps the logger side-effect free unless the app wires
 * something explicit.
 */
export const noopSink: LogSink = {
  write: () => {},
};

/**
 * Console sink. This is the ONE place in the repo where `console.*`
 * calls are legitimate; see the package-local ESLint override.
 *
 * `debug` → `console.debug`
 * `info`  → `console.info`
 * `warn`  → `console.warn`
 * `error` → `console.error`
 *
 * Format: `[LEVEL] message { ...context }` (and the error argument
 * passed through as-is so browser/Node runtimes can render the stack).
 */
export const consoleSink: LogSink = {
  write(record: LogRecord): void {
    const prefix = `[${record.level.toUpperCase()}] ${record.message}`;
    const rest: unknown[] = [];
    if (record.context && Object.keys(record.context).length > 0) {
      rest.push(record.context);
    }
    if (record.error !== undefined) {
      rest.push(record.error);
    }
    switch (record.level) {
      case 'debug':
        console.debug(prefix, ...rest);
        return;
      case 'info':
        console.info(prefix, ...rest);
        return;
      case 'warn':
        console.warn(prefix, ...rest);
        return;
      case 'error':
        console.error(prefix, ...rest);
        return;
    }
  },
};

/**
 * In-memory sink for tests. Records accumulate until `reset()`.
 */
export function memorySink(): LogSink & {
  readonly records: readonly LogRecord[];
  reset(): void;
} {
  const records: LogRecord[] = [];
  return {
    records,
    write(record) {
      records.push(record);
    },
    reset() {
      records.length = 0;
    },
  };
}
